import { type DocumentMimeType, DocumentMimeTypes, type FileData } from "@qendo/profile/files";
import { type DocumentPickerAsset, getDocumentAsync } from "expo-document-picker";
import { useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { Button } from "~/components/Button";
import { FileListingItem } from "~/components/form/gallery/FileListingItem";
import { Title } from "~/components/layout/text";
import { Flex, Gap, Padding } from "~/constants/layout";
import { Radius, Space } from "~/constants/sizing";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";
import { filterUnique } from "~/util/array";
import { getFileCategory } from "~/util/qendo/media";
import { formatFilesize } from "~/util/string";

export type DocumentPickerProps = {
    disabled?: boolean;
    value?: FileData[];
    remove?: string[];
    assets: DocumentPickerAsset[];
    setAssets: (value: DocumentPickerAsset[]) => void;
    setRemove?: (value: string[]) => void;
};

export const DocumentPicker = ({ value, remove, assets, setAssets, setRemove, disabled }: DocumentPickerProps) => {
    const { background, border } = useTheme();

    const files = useMemo(
        () => [
            ...(value ?? []).filter(({ id }) => !(remove ?? []).includes(id)),
            ...assets.map<FileData>((asset) => ({
                id: asset.uri,
                url: asset.uri,
                name: asset.name,
                category: getFileCategory(asset.mimeType),
                filename: asset.name,
                file_size: formatFilesize(asset.size ?? 0),
                mime: asset.mimeType as DocumentMimeType,
            })),
        ],
        [value, remove, assets],
    );

    const removed = useMemo(() => (value ?? []).filter(({ id }) => (remove ?? []).includes(id)), [value, remove]);

    const pickFile = async () => {
        if (disabled) {
            return;
        }

        await getDocumentAsync({
            copyToCacheDirectory: false,
            type: DocumentMimeTypes,
        }).then(({ assets: selected, canceled }) => {
            if (!canceled && filled(selected)) {
                setAssets(
                    [...assets, ...selected].filter(
                        ({ uri }, idx, array) => idx === array.findIndex((a) => a.uri === uri),
                    ),
                );
            }
        });
    };

    const removeFile = (file: string) => {
        if (value?.find(({ id }) => id === file)) {
            setRemove?.([...(remove ?? []), file].filter(filterUnique));
        } else {
            setAssets(assets.filter(({ uri }) => uri !== file));
        }
    };

    const restoreFile = (file: string) => {
        setRemove?.((remove ?? []).filter((id) => id !== file));
    };

    return (
        <>
            {(filled(files) || filled(removed)) && (
                <View style={[styles.fileList, styles.container, background.secondary]}>
                    {files.map((item) => (
                        <FileListingItem key={item.id} item={item} onRequestRemove={removeFile} disabled={disabled} />
                    ))}
                    {filled(removed) && (
                        <View style={Padding.top_sm}>
                            <Title size="sm">Files to delete</Title>
                            <View style={[styles.fileList, styles.removeList, border.tertiary]}>
                                {removed.map((item) => (
                                    <FileListingItem
                                        key={item.id}
                                        item={item}
                                        onRequestRestore={restoreFile}
                                        disabled={disabled}
                                    />
                                ))}
                            </View>
                        </View>
                    )}
                </View>
            )}
            <View style={[Flex.row, Gap.sm]}>
                <Button
                    variant="secondary"
                    disabled={disabled}
                    title="Attach document"
                    icon="paperclip"
                    onPress={pickFile}
                />
            </View>
        </>
    );
};

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: Space.md,
        paddingVertical: Space.sm,
        borderRadius: Radius.lg,
    },
    fileList: {
        paddingTop: Space.sm,
        gap: Space.md,
    },
    removeList: {
        borderTopWidth: 1,
    },
});
